import React, { useState, useEffect, useCallback } from 'react';
import './DiscountCodeManagement.scss';

interface DiscountCode {
  id: number;
  code: string;
  type: 'fixed' | 'percentage';
  value: number;
  min_order_amount: number | null;
  max_uses: number | null;
  used_count: number;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  created_at: string;
}

const emptyForm = {
  code: '',
  type: 'fixed' as 'fixed' | 'percentage',
  value: '',
  min_order_amount: '',
  max_uses: '',
  valid_from: '',
  valid_until: '',
  is_active: true,
};

const DiscountCodeManagement: React.FC = () => {
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const fetchCodes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/discount-codes/admin', { headers: getHeaders() });
      if (!res.ok) throw new Error('Nepavyko užkrauti');
      const data = await res.json();
      setCodes(data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setShowForm(true);
  };

  const openEdit = (code: DiscountCode) => {
    setEditingId(code.id);
    setForm({
      code: code.code,
      type: code.type,
      value: String(code.value),
      min_order_amount: code.min_order_amount != null ? String(code.min_order_amount) : '',
      max_uses: code.max_uses != null ? String(code.max_uses) : '',
      valid_from: code.valid_from ? code.valid_from.slice(0, 16) : '',
      valid_until: code.valid_until ? code.valid_until.slice(0, 16) : '',
      is_active: code.is_active,
    });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.code.trim() || !form.value) {
      setError('Kodas ir reikšmė yra privalomi');
      return;
    }

    const body = {
      code: form.code,
      type: form.type,
      value: parseFloat(form.value),
      min_order_amount: form.min_order_amount ? parseFloat(form.min_order_amount) : null,
      max_uses: form.max_uses ? parseInt(form.max_uses) : null,
      valid_from: form.valid_from || null,
      valid_until: form.valid_until || null,
      is_active: form.is_active,
    };

    try {
      const url = editingId
        ? `/api/discount-codes/admin/${editingId}`
        : '/api/discount-codes/admin';
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, { method, headers: getHeaders(), body: JSON.stringify(body) });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Nepavyko išsaugoti');
        return;
      }

      setShowForm(false);
      fetchCodes();
    } catch (err) {
      setError('Klaida saugant kodą');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Ar tikrai norite ištrinti šį nuolaidos kodą?')) return;

    try {
      const res = await fetch(`/api/discount-codes/admin/${id}`, { method: 'DELETE', headers: getHeaders() });
      if (res.ok) {
        fetchCodes();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleActive = async (code: DiscountCode) => {
    try {
      await fetch(`/api/discount-codes/admin/${code.id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ is_active: !code.is_active }),
      });
      fetchCodes();
    } catch (err) {
      console.error(err);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('lt-LT', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="discount-code-management">
        <h2>Nuolaidų kodai</h2>
        <p>Kraunama...</p>
      </div>
    );
  }

  return (
    <div className="discount-code-management">
      <div className="page-header">
        <div>
          <h2>Nuolaidų kodai</h2>
          <p className="subtitle">Kurkite ir valdykite nuolaidų kodus</p>
        </div>
        <button className="btn-create" onClick={openCreate}>
          + Naujas kodas
        </button>
      </div>

      {showForm && (
        <div className="form-overlay" onClick={() => setShowForm(false)}>
          <div className="form-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingId ? 'Redaguoti kodą' : 'Naujas nuolaidos kodas'}</h3>

            <form onSubmit={handleSubmit}>
              {error && <div className="form-error">{error}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label>Kodas</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="pvz. NUOLAIDA20"
                  />
                </div>
                <div className="form-group">
                  <label>Tipas</label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as 'fixed' | 'percentage' })}>
                    <option value="fixed">Fiksuota suma (€)</option>
                    <option value="percentage">Procentinė (%)</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Reikšmė {form.type === 'fixed' ? '(€)' : '(%)'}</label>
                  <input
                    type="number"
                    step={form.type === 'fixed' ? '0.01' : '1'}
                    min="0"
                    max={form.type === 'percentage' ? '100' : undefined}
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Min. užsakymo suma (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.min_order_amount}
                    onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })}
                    placeholder="Neprivaloma"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Maks. panaudojimų</label>
                  <input
                    type="number"
                    min="0"
                    value={form.max_uses}
                    onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                    placeholder="Neribota"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    />
                    Aktyvus
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Galioja nuo</label>
                  <input
                    type="datetime-local"
                    value={form.valid_from}
                    onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Galioja iki</label>
                  <input
                    type="datetime-local"
                    value={form.valid_until}
                    onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowForm(false)}>
                  Atšaukti
                </button>
                <button type="submit" className="btn-save">
                  {editingId ? 'Atnaujinti' : 'Sukurti'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {codes.length === 0 ? (
        <div className="empty-state">
          <p>Nėra sukurtų nuolaidų kodų</p>
        </div>
      ) : (
        <div className="codes-table-wrapper">
          <table className="codes-table">
            <thead>
              <tr>
                <th>Kodas</th>
                <th>Tipas</th>
                <th>Reikšmė</th>
                <th>Panaudojimai</th>
                <th>Galioja nuo</th>
                <th>Galioja iki</th>
                <th>Būsena</th>
                <th>Veiksmai</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((code) => (
                <tr key={code.id} className={!code.is_active ? 'inactive' : ''}>
                  <td className="code-cell">{code.code}</td>
                  <td>{code.type === 'fixed' ? 'Fiksuota' : 'Procentinė'}</td>
                  <td>
                    {code.type === 'fixed'
                      ? `${Number(code.value).toFixed(2)} €`
                      : `${Number(code.value)}%`}
                  </td>
                  <td>
                    {code.used_count}{code.max_uses != null ? ` / ${code.max_uses}` : ''}
                  </td>
                  <td>{formatDate(code.valid_from)}</td>
                  <td>{formatDate(code.valid_until)}</td>
                  <td>
                    <span
                      className={`status-badge ${code.is_active ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleActive(code)}
                      title="Spustelėkite norėdami perjungti"
                    >
                      {code.is_active ? 'Aktyvus' : 'Neaktyvus'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-edit" onClick={() => openEdit(code)}>
                        Redaguoti
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(code.id)}>
                        Ištrinti
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DiscountCodeManagement;
